import Employee from "../../employees/services/model/Employee";
import { SupportedDocumentQueries } from "../../payslips/services/model/FeatureConfiguration";

export interface InboxPageHookInterface {
  tab?: string;
  page: number;
  pages: number;
  uuid?: string;
  error: unknown;
  openCount: number | null;
  tickets: EmployeeTicket[];
  pageLoaderVisible: boolean;
  resolvedCount: number | null;
  currentCount: number | null;
  ticket: InboxQueryDetails | null;
  setTicket: (ticket: InboxQueryDetails | null) => void;
  setTicketId: (id: string | null) => void;
  setPage: (page: number) => void;
}

export enum InboxQueryStatus {
  OPEN = "OPEN",
  RESOLVED = "RESOLVED",
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  CANCELLED = "CANCELLED",
  DECLINED = "DECLINED",
  UNDEFINED = "UNDEFINED",
}

export enum InboxTabs {
  OPEN = "OPEN",
  RESOLVED = "RESOLVED",
  EXPORTS = "EXPORTS",
}

export enum QueryCategory {
  LEAVE = "LEAVE",
  DOCUMENT = "QUERY",
}

export enum ModalModes {
  Resolve = "resolve",
  Approve = "approve",
  Cancel = "cancel",
  Decline = "decline",
}

export enum ModalActions {
  Resolve = "Mark as Resolved",
  Approve = "Approve",
  Decline = "Decline",
  Cancel = "Cancel",
}

export interface QueryHistory {
  actioner_level: number;
  actioned_by: {
    id: number;
    first_name: string;
    last_name: string;
  };
  timestamp: string;
  action: "DECLINED" | "APPROVED" | "CANCELLED";
  outcome?: string; // Optional
  message?: string; // Optional
  comment?: string; // Optional
}

export interface EmployeeTicket {
  id: string;
  query_type:
    | SupportedDocumentQueries.PAYSLIP_QUERY
    | SupportedDocumentQueries.ROSTER_QUERY
    | SupportedDocumentQueries.TIMESHEET_QUERY
    | string;
  date_created: string;
  date_updated: string;
  document_id: number;
  first_name: string | null;
  last_name: string | null;
  employee_no: string | null;
  employer_branch_name: string | null;
  data: WhatsAppFlowDataItem[];
  status: InboxQueryStatus;
  history?: QueryHistory[];
  category: QueryCategory;
  employee: Employee;
}

export interface InboxQueryListApiRequest {
  page: number;
  activeTab: InboxTabs;
  fetchingFromETSEnabled?: boolean; // Optional, used for fetching from Employee Ticketing System
  queryResolutionDataEnabled?: boolean; // Optional, used for enabling query resolution data
  search?: string; // Optional, used for searching tickets
  appliedFilters?: InboxFilterState; // Optional, used for filtering tickets
}

export interface InboxQueryApiRequest {
  id: string;
}

export interface InboxQueryListApiResponse {
  count: number;
  total_pages: number;
  current_page: number;
  next_page: string | null;
  previous_page: string | null;
  results: EmployeeTicket[];
}

export interface InboxQueryResolveApiRequest {
  status: InboxQueryStatus;
  resolve: string; // Ticket ID
  query_outcome?: string; // Query Outcome (Fetched from Strapi)
  message_to_employee?: string; // Message to Employee
  queryResolutionDataEnabled?: boolean; // Flag to enable query resolution data
}

export interface InboxQueryResolveApiResponse {
  message: string;
}

export interface ExportJobApiResponse {
  message: string;
}

export interface WhatsAppFlowDataItem {
  key: string;
  label: string | null;
  type: string | null;
  value: string | null;
}

export interface InboxQueryDetails extends EmployeeTicket {
  query_id?: string;
  site_name: string;
  query_reason: string;
  other_information: string | null;
  issue_date: string;
  period_start_date: string;
  period_end_date: string;
  document_uploaded_at: string;
  document_updated_at: string;
  document_changed_since_query: boolean;
  document_uploaded_by:
    | {
        first_name: string;
        last_name: string;
        username: string;
      }
    | null
    | undefined;
  document_asset_link: string;
  supporting_document_asset_link: string | null | undefined;
  // Below are the fields for WhatsApp Flow, and will only be present if the query is from WhatsApp Flows
  flow_token: string | null;
  action: string | null;
  file_query_proof: string[]; // Returns a merged array of both file_query_proof and file_other_query_proof files
  file_leave_proof: string[]; // Returns a merged array of both file_leave_proof and file_other_leave_proof files
  final_approver: boolean | null;
  // Whether the logged-in user is the current approver and can action (approve/decline) this ticket
  // right now. Sourced from the ticketing service; may be absent on older backends, in which case
  // it is treated as "unknown" and the existing status-based button gating applies unchanged.
  can_action?: boolean | null;
}

export interface EmployeeTicketDetailsProps {
  ticket: InboxQueryDetails;
  setTicketId: (id: string | null) => void;
  setTicket: (ticket: InboxQueryDetails | null) => void;
  inboxQueryTab: InboxTabs;
}

export interface EmployeeTicketsListProps {
  page: number;
  pages: number;
  count: number;
  tickets: EmployeeTicket[];
  ticketId: string | null | undefined;
  setPage: (page: number) => void;
  setTicketId: (ticketId: string) => void;
  setTicket?: (ticket: InboxQueryDetails | null) => void;
  searchQuery?: string;
}

export interface QueryOption {
  value: string;
  label: string;
}

export interface QueryOutcome {
  title: string;
  active: boolean;
  order: number;
  Category: QueryCategory;
}

export enum ExportJobStatus {
  SUBMITTED = "SUBMITTED",
  PROCESSING = "PROCESSING",
  PROCESSED = "PROCESSED",
}

export interface LeaveExport {
  id: string;
  job_name: string;
  employer_id: string;
  date_created: string;
  requested_by_id: number;
  status: ExportJobStatus;
  export_file_path?: string;
}

export interface LeaveExportsListProps {
  page: number;
  pages: number;
  exportCount: number;
  exports: LeaveExport[];
  setPage: (page: number) => void;
}

export interface LeaveExportsListApiResponse {
  count: number; // Total number of export jobs
  total_pages: number; // Total number of pages available
  current_page: number; // Current page number
  next_page: string | null; // Page number for the next page, or null if there are no more pages
  previous_page: string | null; // Page number for the previous page, or null if on the first page
  results: LeaveExport[];
}

export interface CreateLeaveExportJobRequest {
  jobName: string;
}

export interface CreateLeaveExportJobResponse {
  status: string;
}

export interface FilterOption {
  value: string;
  display_name: string;
}

export interface FilterOptionsResponse {
  query_types: FilterOption[];
  leave_types: FilterOption[];
  leave_statuses: FilterOption[];
}

export interface InboxFilterState {
  queriesEnabled: boolean;
  leaveRequestsEnabled: boolean;
  selectedQueryTypes: string[];
  selectedLeaveTypes: string[];
  selectedLeaveStatuses: string[];
}

export interface SerializedFilters {
  categories?: string; // "queries|leave" format
  queryTypes?: string; // pipe-separated query type values
  leaveTypes?: string; // pipe-separated leave type values
  statuses?: string; // pipe-separated status values
}
